import { IRowHookInput, IRowHookOutput } from 'dromo-uploader-react'
import { ITableMessage } from 'dromo-uploader-js'
import { SiteAction } from 'common/types'
import { badGeocodeMsg, failedGeocodeMsg } from './Geocoder'
import { dupParentMsg, dupChildMsg } from './Deduplication.consts'
import {
  missingSoldToWarning,
  highPriorityFields,
} from '../../SolarLeaderboardUpload/FieldSchema'

export interface RowValidations {
  [key: string]: (value: string, row?: IRowHookInput['row']) => ITableMessage[]
}

const missingDataFieldWarning = 'Please provide this value if possible.'
const missingDataRecordWarning =
  'This site is missing valuable data. Please provide the missing value(s) if possible.'

export function updateCellMessages(
  record: IRowHookInput,
  validations: RowValidations
): IRowHookOutput {
  const recordClone = { ...record }
  const isIgnore = recordClone.row.siteAction.value === SiteAction.Ignore
  let isMissingData = false

  for (const key of Object.keys(recordClone.row)) {
    const originalMsgs = recordClone.row[key].info ?? []

    if (isIgnore) {
      // if the user wants to ignore this record, remove all error messages
      const filteredMsgs = originalMsgs.filter((msg) => msg.level !== 'error')
      recordClone.row[key].info = filteredMsgs
      continue
    }
    // keep any info/warning messages and any error messages from other validation logic
    let filteredMsgs = originalMsgs.filter(
      (msg) =>
        (msg.message === badGeocodeMsg ||
          msg.message === failedGeocodeMsg ||
          msg.level !== 'error') &&
        msg.message != missingSoldToWarning &&
        msg.message != missingDataFieldWarning
    )
    if (key === 'siteAction') {
      // for the site action cell, remove the dup warnings and missing data warning when the user updates the row
      filteredMsgs = filteredMsgs.filter(
        (msg) =>
          ![dupChildMsg, dupParentMsg, missingDataRecordWarning].includes(
            msg.message
          )
      )
    }
    if (highPriorityFields.includes(key)) {
      // if this is a field that we want to draw attention to if data is missing, add a warning message
      if (!recordClone.row[key].value) {
        filteredMsgs.push({
          message: missingDataFieldWarning,
          level: 'warning',
        })
        isMissingData = true
      }
    }

    const validator = validations[key]
    if (!validator) {
      recordClone.row[key].info = filteredMsgs
      continue
    }
    const value = recordClone.row[key].value as unknown
    // validate the value and add messages if needed
    recordClone.row[key].info = validator(
      value ? String(value) : '',
      recordClone.row
    ).concat(filteredMsgs)
  }

  if (isMissingData) {
    // add a warning to the site action cell to draw attention to the missing data
    recordClone.row['siteAction'].info?.push({
      message: missingDataRecordWarning,
      level: 'warning',
    })
  }

  return recordClone
}
